import type { ReadonlySignal } from '@preact/signals-core';

import { LEFT_TOOL_BAR_WIDTH } from '../consts.js';
import type { TableViewSelectionWithType } from '../selection.js';

type LayoutProperty = {
  id: string;
  width$: ReadonlySignal<number>;
};

export function createVirtualTableLayoutSnapshot<
  Property extends LayoutProperty,
>(properties: readonly Property[], viewportWidth: number) {
  const dataColumns = properties.map(property => ({
    id: property.id,
    property,
    width: property.width$.value,
  }));
  const dataColumnsWidth = dataColumns.reduce(
    (total, column) => total + column.width + 1,
    0
  );
  const trailingWidth = Math.max(
    40,
    viewportWidth - LEFT_TOOL_BAR_WIDTH - dataColumnsWidth
  );

  return {
    dataColumns,
    gridColumns: [
      { id: 'row-header', width: LEFT_TOOL_BAR_WIDTH },
      ...dataColumns.map(column => ({
        id: column.id,
        width: column.width + 1,
      })),
      { id: 'row-last', width: trailingWidth },
    ],
    propertyIds: dataColumns.map(column => column.id),
  };
}

function reconcileColumnIndex(
  index: number,
  previousPropertyIds: readonly string[],
  nextPropertyIds: readonly string[]
) {
  const previousId = previousPropertyIds[index];
  const retainedIndex = previousId ? nextPropertyIds.indexOf(previousId) : -1;
  return retainedIndex >= 0
    ? retainedIndex
    : Math.max(0, Math.min(index, nextPropertyIds.length - 1));
}

export function reconcileTableSelectionAfterColumnChange(
  selection: TableViewSelectionWithType | undefined,
  previousPropertyIds: readonly string[],
  nextPropertyIds: readonly string[]
): TableViewSelectionWithType | undefined {
  if (!selection || selection.selectionType !== 'area') {
    return selection;
  }
  if (nextPropertyIds.length === 0) {
    return undefined;
  }

  const previousFocusId = previousPropertyIds[selection.focus.columnIndex];
  const focusedColumnWasDeleted =
    previousFocusId != null && !nextPropertyIds.includes(previousFocusId);
  const start = reconcileColumnIndex(
    selection.columnsSelection.start,
    previousPropertyIds,
    nextPropertyIds
  );
  const end = reconcileColumnIndex(
    selection.columnsSelection.end,
    previousPropertyIds,
    nextPropertyIds
  );

  return {
    ...selection,
    columnsSelection: {
      start: Math.min(start, end),
      end: Math.max(start, end),
    },
    focus: {
      ...selection.focus,
      columnIndex: reconcileColumnIndex(
        selection.focus.columnIndex,
        previousPropertyIds,
        nextPropertyIds
      ),
    },
    isEditing: focusedColumnWasDeleted ? false : selection.isEditing,
  };
}

/** Reveal a column in the shared scroll owner, never in the sticky header clone. */
export function getColumnRevealScrollDelta(input: {
  columns: readonly { id: string; width: number }[];
  columnId: string;
  padding: number;
  scrollLeft: number;
  viewportWidth: number;
}): number {
  let left = input.padding;
  for (const column of input.columns) {
    if (column.id === input.columnId) {
      const visibleLeft = input.scrollLeft + input.padding;
      const visibleRight =
        input.scrollLeft + input.viewportWidth - input.padding;
      if (left < visibleLeft || column.width > visibleRight - visibleLeft) {
        return left - visibleLeft;
      }
      return Math.max(0, left + column.width - visibleRight);
    }
    left += column.width;
  }
  return 0;
}
